import { texteEnLangue } from '@artosera/shared';
import { Badge, Button, Select } from '@artosera/ui';
import {
  formatDateRange,
  locale,
  TYPES_EXPOSITION,
  type Langue,
  type TypeExposition,
} from '@artosera/shared';
import { Plus, Search } from 'lucide-react';
import type { JSX } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { estApiError } from '../../api/errors.js';
import {
  useExpositions,
  type ExpositionPresentee,
  type FiltresExpositions,
} from '../../api/expositions.js';
import { useLieux } from '../../api/lieux.js';
import { galerieALaction } from '../../auth/sujet.js';
import { BarreCalendrier } from '../../composants/calendrier/BarreCalendrier.js';
import { Calendrier } from '../../composants/calendrier/Calendrier.js';
import {
  aujourdhuiParis,
  deplacerAncre,
  VUES_CALENDRIER,
} from '../../composants/calendrier/periodes.js';
import { useVueCalendrier } from '../../composants/calendrier/useVueCalendrier.js';
import { useShellContext } from '../shell/context.js';
import { useDebounce } from '../oeuvres/useDebounce.js';
import { evenementsDeLExposition } from './calendrierExpositions.js';
import { VisuelExposition } from './VisuelExposition.js';
import { ETATS_AFFICHES, teinteDeLEtat } from './etatUi.js';
import { NouvelleExpositionDialog } from './NouvelleExpositionDialog.js';
import styles from './ExpositionsPage.module.css';

/**
 * Feuillet de calendrier de la carte : mois, jour et année d'ouverture, à
 * l'heure de Paris comme `formatDateRange` (purement visuel, la plage de
 * dates complète reste écrite à côté).
 */
function calendrier(iso: string, langue: Langue): { mois: string; jour: string; annee: string } {
  const date = new Date(iso);
  const partie = (options: Intl.DateTimeFormatOptions): string =>
    new Intl.DateTimeFormat(locale(langue), { ...options, timeZone: 'Europe/Paris' }).format(date);
  return {
    mois: partie({ month: 'short' }).replace('.', ''),
    jour: partie({ day: 'numeric' }),
    annee: partie({ year: 'numeric' }),
  };
}

/**
 * Expositions — liste (3.3, point 4, S3-d) : groupée par état (à venir / en
 * cours / passées / brouillons), filtrable par type, recherche et état,
 * archivées seulement sur demande (§ 15.4).
 */
export function ExpositionsPage(): JSX.Element {
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const langue = i18n.language === 'en' ? 'en' : 'fr';
  const navigate = useNavigate();
  const { galerieSlug, galerie } = useShellContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const { vue, ancre, aller } = useVueCalendrier('expositions');

  const [recherche, setRecherche] = useState('');
  const rechercheDebattue = useDebounce(recherche, 300);
  const [type, setType] = useState('');
  const [etat, setEtat] = useState('');
  const [inclureArchivees, setInclureArchivees] = useState(false);

  const filtres: FiltresExpositions = useMemo(
    () => ({
      ...(rechercheDebattue ? { q: rechercheDebattue } : {}),
      ...(type ? { type: type as TypeExposition } : {}),
      inclureArchivees,
      parPage: 200,
    }),
    [rechercheDebattue, type, inclureArchivees],
  );

  const liste = useExpositions(galerieSlug, filtres);
  const lieux = useLieux(galerieSlug);
  const peutEcrire = galerieALaction(galerie, 'exhibitions:write');

  const expositions = liste.data?.expositions ?? [];
  const visibles = etat
    ? expositions.filter((exposition) => exposition.etat === etat)
    : expositions;

  const groupes = useMemo(() => {
    const actives = visibles.filter((exposition) => !exposition.archive);
    const archivees = visibles.filter((exposition) => exposition.archive);
    const parEtat = new Map<string, ExpositionPresentee[]>();
    for (const code of ETATS_AFFICHES) parEtat.set(code, []);
    for (const exposition of actives) {
      parEtat.get(exposition.etat)?.push(exposition);
    }
    const resultat: { cle: string; libelle: string; expositions: ExpositionPresentee[] }[] =
      ETATS_AFFICHES.map((code) => ({
        cle: code,
        libelle: t(`regie:expositions.groupes.${code}`),
        expositions: parEtat.get(code) ?? [],
      }));
    if (inclureArchivees && archivees.length > 0) {
      resultat.push({
        cle: 'archivees',
        libelle: t('regie:expositions.groupes.archivees'),
        expositions: archivees,
      });
    }
    return resultat.filter((groupe) => groupe.expositions.length > 0);
  }, [visibles, inclureArchivees, t]);

  const evenementsCalendrier = useMemo(
    () =>
      visibles.flatMap((exposition) =>
        evenementsDeLExposition(exposition, {
          langue,
          galerieSlug,
          titreVernissage: (titre) => t('regie:calendrier.vernissage', { titre }),
        }),
      ),
    [visibles, langue, galerieSlug, t],
  );

  const nouvelleOuverte = searchParams.get('nouvelle') === '1';
  function fermerNouvelle(): void {
    const suivants = new URLSearchParams(searchParams);
    suivants.delete('nouvelle');
    setSearchParams(suivants, { replace: true });
  }

  function reinitialiserFiltres(): void {
    setRecherche('');
    setType('');
    setEtat('');
    setInclureArchivees(false);
  }

  return (
    <div className={styles.page}>
      <div className={styles.entete}>
        <h1 className={styles.titre}>{t('regie:expositions.titre')}</h1>
        {peutEcrire ? (
          <Button
            size="sm"
            onClick={() => {
              const suivants = new URLSearchParams(searchParams);
              suivants.set('nouvelle', '1');
              setSearchParams(suivants);
            }}
          >
            <Plus size={16} strokeWidth={1.5} aria-hidden />
            {t('regie:expositions.nouvelleExposition')}
          </Button>
        ) : null}
      </div>

      <BarreCalendrier
        vue={vue}
        vues={VUES_CALENDRIER}
        ancre={ancre}
        langue={langue}
        navigation={vue !== 'liste'}
        onVue={(suivante) => aller(suivante, ancre)}
        onDeplacer={(sens) => aller(vue, sens === 0 ? undefined : deplacerAncre(vue, ancre, sens))}
      />

      <div
        className={styles.filtres}
        role="search"
        aria-label={t('regie:expositions.filtres.titre')}
      >
        <div className={styles.champRecherche}>
          <Search size={16} strokeWidth={1.5} aria-hidden />
          <input
            type="search"
            className={styles.saisieRecherche}
            placeholder={t('regie:expositions.rechercheChamp')}
            aria-label={t('regie:expositions.rechercheChamp')}
            value={recherche}
            onChange={(event) => setRecherche(event.target.value)}
          />
        </div>

        <div className={styles.filtreItem}>
          <Select
            aria-label={t('regie:expositions.filtres.type')}
            value={type}
            onValueChange={setType}
            placeholder={t('regie:expositions.filtres.tousLesTypes')}
            options={[
              { value: '', label: t('regie:expositions.filtres.tousLesTypes') },
              ...TYPES_EXPOSITION.map((code) => ({
                value: code,
                label: t(`regie:expositions.types.${code}`),
              })),
            ]}
          />
        </div>

        <div className={styles.filtreItem}>
          <Select
            aria-label={t('regie:expositions.filtres.etat')}
            value={etat}
            onValueChange={setEtat}
            placeholder={t('regie:expositions.filtres.tousLesEtats')}
            options={[
              { value: '', label: t('regie:expositions.filtres.tousLesEtats') },
              ...ETATS_AFFICHES.map((code) => ({
                value: code,
                label: t(`regie:expositions.etats.${code}`),
              })),
            ]}
          />
        </div>

        <label className={styles.caseArchivees}>
          <input
            type="checkbox"
            checked={inclureArchivees}
            onChange={(event) => setInclureArchivees(event.target.checked)}
          />
          {t('regie:expositions.filtres.inclureArchivees')}
        </label>

        <Button
          variant="ghost"
          size="sm"
          className={styles.reinitialiser}
          onClick={reinitialiserFiltres}
        >
          {t('regie:expositions.filtres.reinitialiser')}
        </Button>
      </div>

      {liste.isLoading ? (
        <p className={styles.vide}>{t('commun:chargement')}</p>
      ) : liste.isError ? (
        <p className={styles.vide} role="alert">
          {estApiError(liste.error) ? liste.error.message : t('commun:erreurReseau')}
        </p>
      ) : vue !== 'liste' ? (
        <Calendrier
          vue={vue}
          ancre={ancre}
          evenements={evenementsCalendrier}
          aujourdhui={aujourdhuiParis()}
          langue={langue}
          onAller={aller}
        />
      ) : groupes.length === 0 ? (
        <p className={styles.vide}>{t('regie:expositions.aucunResultat')}</p>
      ) : (
        groupes.map((groupe) => (
          <section
            key={groupe.cle}
            className={styles.groupe}
            data-etat={groupe.cle}
            aria-label={groupe.libelle}
          >
            <h2 className={styles.groupeTitre}>
              <span className={styles.groupeRepere} aria-hidden />
              {groupe.libelle}
              <span className={styles.groupeCompte}>{groupe.expositions.length}</span>
            </h2>
            <ul className={styles.grille}>
              {groupe.expositions.map((exposition) => {
                const debut = calendrier(exposition.debutLe, langue);
                return (
                  <li key={exposition.id}>
                    <Link
                      className={styles.carte}
                      data-etat={exposition.etat}
                      to={`/${galerieSlug}/expositions/${exposition.id}`}
                    >
                      <span className={styles.calendrier} aria-hidden>
                        <span className={styles.calendrierMois}>{debut.mois}</span>
                        <span className={styles.calendrierJour}>{debut.jour}</span>
                        <span className={styles.calendrierAnnee}>{debut.annee}</span>
                      </span>

                      <span className={styles.carteCorps}>
                        <span className={styles.carteEntete}>
                          <Badge tone="neutral">
                            {t(`regie:expositions.types.${exposition.type}`)}
                          </Badge>
                          <Badge tone={teinteDeLEtat(exposition.etat)}>
                            {t(`regie:expositions.etats.${exposition.etat}`)}
                          </Badge>
                        </span>
                        <span className={styles.carteTitre}>
                          {texteEnLangue(exposition.titre, langue)}
                        </span>
                        <span className={styles.carteMeta}>
                          <span>
                            {formatDateRange(
                              new Date(exposition.debutLe),
                              new Date(exposition.finLe),
                              langue,
                            )}
                          </span>
                          <span className={styles.carteLieu}>
                            {exposition.stand
                              ? t('regie:expositions.carte.stand', { stand: exposition.stand })
                              : (lieux.data?.find((lieu) => lieu.id === exposition.lieuId)?.nom ??
                                '—')}
                          </span>
                        </span>
                        {exposition.texte?.fr ? (
                          <span className={styles.carteTexte}>{exposition.texte.fr}</span>
                        ) : null}
                      </span>

                      <span className={styles.carteVisuel}>
                        <VisuelExposition
                          galerie={galerieSlug}
                          expositionId={exposition.id}
                          oeuvres={exposition.oeuvres}
                          type={exposition.type}
                          stand={exposition.stand}
                        />
                        <span className={styles.cartePied}>
                          <span className={styles.carteCompte}>
                            {t('regie:expositions.carte.oeuvresCompte', {
                              count: exposition.oeuvres.length,
                            })}
                          </span>
                          <Badge tone={exposition.visibleSurLeSite ? 'available' : 'neutral'}>
                            {exposition.visibleSurLeSite
                              ? t('regie:expositions.carte.visible')
                              : t('regie:expositions.carte.masquee')}
                          </Badge>
                        </span>
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </section>
        ))
      )}

      <NouvelleExpositionDialog
        key={nouvelleOuverte ? 'nouvelle-ouverte' : 'nouvelle-fermee'}
        galerieSlug={galerieSlug}
        open={nouvelleOuverte}
        onOpenChange={(ouvert) => {
          if (!ouvert) fermerNouvelle();
        }}
        onCreee={(id) => {
          fermerNouvelle();
          navigate(`/${galerieSlug}/expositions/${id}`);
        }}
      />
    </div>
  );
}
